// Unified "contribute" bottom-sheet. Opens from a floating "+" button on
// the map (ContributeFab). Three tabs:
//   1. Rate this spot — quick 1-5 face + tag chips → community_tags
//   2. Photo — opens the photo-pin dialog at the chosen location
//   3. Tip — opens the leave-a-tip dialog at the chosen location
//
// Friction-light by design: location auto-fills from GPS, an address
// search, or a tap-on-map. The "Pretty area" nomination flow used to
// live here but was redundant with rating, so it was removed.

import { useEffect, useRef, useState } from "react";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Slider } from "@/components/ui/slider";
import { Camera, Crosshair, Flag, Hand, Loader2, MapPin, MessageSquare, Search, Signpost, Sparkles, Star } from "lucide-react";
import { toast } from "sonner";
import { submitTag, type Sentiment } from "@/api/community";
import { submitRoadNomination } from "@/api/roadNominations";
import { nominateStop } from "@/api/nominatedStops";
import { geocodeAutocomplete, type GeoSuggestion, type TravelMode, resolveSuggestionCoords, needsResolve, endAutocompleteSession } from "@/api/routing";
import type { LngLat } from "@/utils/polyline";
import { useAuth } from "@/hooks/useAuth";
import { QuickAuthDialog } from "@/components/QuickAuthDialog";
import { useRoadContribution, sourceForRoad } from "@/hooks/useRoadContribution";
import { enqueueTag } from "@/utils/pendingContributions";

// ---------------------------------------------------------------------------
// Draft persistence — keep the user's in-progress rating safe across the
// sign-in prompt, accidental dialog dismissals, and even tab reloads.
// ---------------------------------------------------------------------------
const DRAFT_KEY = "pleasant.contributeDraft.v1";
const DRAFT_TTL_MS = 24 * 60 * 60 * 1000;

interface ContributeDraft {
  rating: number | null;
  tags: string[];
  note: string;
  coord?: LngLat;
  coordLabel?: string;
  mode: TravelMode;
  savedAt: number;
}

function readDraft(): ContributeDraft | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(DRAFT_KEY);
    if (!raw) return null;
    const d = JSON.parse(raw) as ContributeDraft;
    if (!d || typeof d.savedAt !== "number") return null;
    if (Date.now() - d.savedAt > DRAFT_TTL_MS) {
      window.localStorage.removeItem(DRAFT_KEY);
      return null;
    }
    return d;
  } catch {
    return null;
  }
}

function writeDraft(d: ContributeDraft): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(DRAFT_KEY, JSON.stringify(d));
  } catch {
    /* quota — best effort */
  }
}

function clearDraft(): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.removeItem(DRAFT_KEY);
  } catch {
    /* ignore */
  }
}

function draftHasContent(d: Pick<ContributeDraft, "rating" | "tags" | "note">): boolean {
  return d.rating !== null || d.tags.length > 0 || d.note.trim().length > 0;
}

interface Props {
  open: boolean;
  onClose: () => void;
  /** Best guess for where to attach the contribution. Falls back to GPS. */
  defaultLocation?: LngLat;
  mode: TravelMode;
  /** Opens the photo-pin dialog. When omitted, the Photo tab is hidden. */
  onAddPhoto?: () => void;
  /** Opens the leave-a-tip dialog at the picked spot. When omitted, hidden. */
  onAddTip?: (coord: LngLat, label?: string) => void;
  /** When set, shows a "Pick on map" button that closes the sheet and asks
   *  the parent to start a tap-to-pick interaction. Parent should re-open
   *  the sheet (with new defaultLocation) once the user taps. */
  onPickOnMap?: () => void;
}

const REACTIONS: { rating: number; label: string; sentiment: Sentiment }[] = [
  { rating: 1, label: "😖 Rough",     sentiment: "negative" },
  { rating: 2, label: "😕 Meh",       sentiment: "negative" },
  { rating: 3, label: "🙂 Okay",      sentiment: "positive" },
  { rating: 4, label: "😊 Nice",      sentiment: "positive" },
  { rating: 5, label: "🤩 Gorgeous",  sentiment: "positive" },
];

// Ordered worst → best. `kind` drives the chip color: blue for negative,
// green for positive.
const QUICK_TAGS: { label: string; kind: "neg" | "pos" }[] = [
  { label: "Sketchy",         kind: "neg" },
  { label: "Heavy traffic",   kind: "neg" },
  { label: "Too loud",        kind: "neg" },
  { label: "Bad sidewalk",    kind: "neg" },
  { label: "Pretty street",   kind: "pos" },
  { label: "Tree-lined",      kind: "pos" },
  { label: "Water view",      kind: "pos" },
  { label: "Pretty farm",     kind: "pos" },
  { label: "Historic",        kind: "pos" },
  { label: "Hidden gem",      kind: "pos" },
  { label: "Public bathroom", kind: "pos" },
];

export const ContributeSheet = ({ open, onClose, defaultLocation, mode, onAddPhoto, onAddTip, onPickOnMap }: Props) => {
  const [tab, setTab] = useState<"rate" | "photo" | "tip" | "road" | "stop">("rate");
  const [coord, setCoord] = useState<LngLat | undefined>(defaultLocation);
  const [coordLabel, setCoordLabel] = useState<string | undefined>();
  // R1: how we came by `coord` decides which confidence gates apply.
  //   "gps"    — a fix; its accuracy must pass the 25 m gate to auto-resolve
  //   "placed" — the user tapped the map or picked an address, so there is no
  //              fix quality to judge and the accuracy gate is skipped
  const [coordAccuracyM, setCoordAccuracyM] = useState<number | null>(null);
  const [coordFromGps, setCoordFromGps] = useState(false);
  // The one gate every road contribution from this sheet passes through.
  const { requestRoad, picker: roadPicker } = useRoadContribution();
  const [busy, setBusy] = useState(false);
  const [locating, setLocating] = useState(false);

  // Address autocomplete state
  const [addrQuery, setAddrQuery] = useState("");
  const [addrSuggestions, setAddrSuggestions] = useState<GeoSuggestion[]>([]);
  const [addrSearching, setAddrSearching] = useState(false);
  const addrAbortRef = useRef<number>(0);

  // Rate-a-spot state
  const [rating, setRating] = useState<number | null>(null);
  const [tags, setTags] = useState<string[]>([]);
  const [note, setNote] = useState("");

  // Worth-a-stop state ("Worth a stop" community detours, Morgan Aug 4 —
  // ships in build 3). Nominations land in nominated_stops and appear as
  // "Local pick" chips on any route passing within the detours corridor,
  // where the community votes them up or down.
  const [stopName, setStopName] = useState("");
  const [stopWhy, setStopWhy] = useState("");

  // Nominate-a-great-road state.
  const [roadName, setRoadName] = useState("");
  const [roadWhy, setRoadWhy] = useState("");
  // Road-name search (Morgan: "just say type in the road — we search the
  // town on our end and let the user choose"). Typing the name searches
  // near the user; picking a match sets the location automatically, so
  // pin-dropping becomes a fallback rather than a requirement.
  const [roadSuggestions, setRoadSuggestions] = useState<GeoSuggestion[]>([]);
  const [roadSearchBusy, setRoadSearchBusy] = useState(false);
  const [roadPickedPlace, setRoadPickedPlace] = useState<string | null>(null);
  const roadDebounceRef = useRef<number>();
  useEffect(() => {
    window.clearTimeout(roadDebounceRef.current);
    if (tab !== "road") { setRoadSuggestions([]); return; }
    const q = roadName.trim();
    if (q.length < 3 || roadPickedPlace) { setRoadSuggestions([]); setRoadSearchBusy(false); return; }
    setRoadSearchBusy(true);
    roadDebounceRef.current = window.setTimeout(async () => {
      try {
        const r = await geocodeAutocomplete(q, coord ?? defaultLocation);
        setRoadSuggestions(r.slice(0, 5));
      } catch {
        setRoadSuggestions([]);
      } finally {
        setRoadSearchBusy(false);
      }
    }, 400);
    return () => window.clearTimeout(roadDebounceRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [roadName, tab, roadPickedPlace]);

  const pickRoadSuggestion = async (s: GeoSuggestion) => {
    let coords = s.center;
    if (needsResolve(s)) {
      try { coords = await resolveSuggestionCoords(s); } catch { /* keep center */ }
    }
    if (coords) {
      setCoord(coords);
      setCoordLabel(s.place || s.name);
    }
    setRoadName(s.name);
    setRoadPickedPlace(s.place || s.name);
    setRoadSuggestions([]);
    endAutocompleteSession();
  };

  // Auth gate — tag contributions are only reliably stored when tied to a
  // signed-in account, so we prompt sign-in and resume the submit on success.
  const { user, loading: authLoading } = useAuth();
  const [authOpen, setAuthOpen] = useState(false);
  const pendingActionRef = useRef<(() => void) | null>(null);

  // Reset on open and try to fetch fresh GPS as a backup if nothing passed in.
  // If a draft from a prior session/dialog dismissal is still around, restore
  // it so the user doesn't lose their tags/rating/note.
  //
  // RUNS ONCE PER OPEN — refs, not deps, for location and mode. During a
  // drive `defaultLocation` follows the car, so having it as a dependency
  // re-ran this reset every GPS tick: the sheet wiped whatever Morgan was
  // typing and re-toasted "Restored your in-progress rating" on loop
  // (Umbria, Aug 6: "it doesn't work and says something over and over
  // again on the bottom").
  const defaultLocationRef = useRef(defaultLocation);
  defaultLocationRef.current = defaultLocation;
  const modeRef = useRef(mode);
  modeRef.current = mode;
  useEffect(() => {
    if (!open) return;
    const defaultLocation = defaultLocationRef.current;
    const mode = modeRef.current;
    setTab("rate");
    const draft = readDraft();
    const useDraft = draft && draft.mode === mode && draftHasContent(draft);
    setRating(useDraft ? draft.rating : null);
    setTags(useDraft ? draft.tags : []);
    setNote(useDraft ? draft.note : "");
    const restoredCoord = useDraft && draft.coord ? draft.coord : defaultLocation;
    setCoord(restoredCoord);
    // A restored draft or a host-supplied default carries no fix quality, so it
    // cannot auto-resolve on accuracy — it goes through the picker instead.
    setCoordAccuracyM(null);
    setCoordFromGps(false);
    setCoordLabel(
      useDraft && draft.coordLabel
        ? draft.coordLabel
        : defaultLocation
          ? "Suggested spot"
          : undefined,
    );
    setAddrQuery("");
    setAddrSuggestions([]);
    if (useDraft) {
      toast("Restored your in-progress rating", { duration: 2500 });
    }
    if (!restoredCoord && navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          setCoord([pos.coords.longitude, pos.coords.latitude]);
          setCoordLabel("Your current location");
        },
        () => {},
        { enableHighAccuracy: false, timeout: 4000, maximumAge: 60_000 },
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- once per open, by design (see comment above)
  }, [open]);

  // Persist the draft whenever the rating/tags/note/coord change while the
  // sheet is open. This keeps the in-progress contribution safe across the
  // sign-in dialog, accidental sheet dismissals, and even full reloads.
  useEffect(() => {
    if (!open) return;
    if (!draftHasContent({ rating, tags, note })) {
      // Nothing meaningful to save yet — and clear any stale draft so
      // closing an empty sheet doesn't leave a ghost behind.
      clearDraft();
      return;
    }
    writeDraft({ rating, tags, note, coord, coordLabel, mode, savedAt: Date.now() });
  }, [open, rating, tags, note, coord, coordLabel, mode]);

  // Debounced address autocomplete.
  useEffect(() => {
    const q = addrQuery.trim();
    if (q.length < 2) { setAddrSuggestions([]); return; }
    const id = ++addrAbortRef.current;
    setAddrSearching(true);
    const handle = window.setTimeout(async () => {
      try {
        const r = await geocodeAutocomplete(q, defaultLocation);
        if (id === addrAbortRef.current) setAddrSuggestions(r);
      } finally {
        if (id === addrAbortRef.current) setAddrSearching(false);
      }
    }, 400);
    return () => window.clearTimeout(handle);
  }, [addrQuery, defaultLocation]);

  const useMyLocation = () => {
    if (!navigator.geolocation) {
      toast.error("Location not available");
      return;
    }
    setLocating(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setCoord([pos.coords.longitude, pos.coords.latitude]);
        setCoordAccuracyM(
          typeof pos.coords.accuracy === "number" ? pos.coords.accuracy : null,
        );
        setCoordFromGps(true);
        setCoordLabel("Your current location");
        setLocating(false);
      },
      () => {
        // Native iOS / Capacitor uses iPhone Settings → Pleasant Routes →
        // Location, not "browser permissions". Web build uses browser
        // settings. Use the shared helper so we get the right message.
        toast.error("Couldn't get your location", {
          description: "Check Location permission in iPhone Settings → Pleasant Routes.",
        });
        setLocating(false);
      },
      { enableHighAccuracy: true, timeout: 8000, maximumAge: 30_000 },
    );
  };

  const pickAddress = async (s: GeoSuggestion) => {
    const coords = needsResolve(s) ? await resolveSuggestionCoords(s) : s.center;
    endAutocompleteSession();
    setCoord(coords);
    setCoordAccuracyM(null);
    setCoordFromGps(false);      // an address the user chose, not a fix
    setCoordLabel(s.name);
    setAddrQuery("");
    setAddrSuggestions([]);
  };

  const toggleTag = (t: string) =>
    setTags((cur) => (cur.includes(t) ? cur.filter((x) => x !== t) : [...cur, t]));

  const submitRating = async () => {
    if (!coord) {
      toast.error("Need a location", { description: "Allow location access or pick a spot on the map first." });
      return;
    }
    if (!rating) {
      toast.error("Pick a rating");
      return;
    }
    if (!authLoading && !user) {
      // Stash the action, prompt for sign-in, and resume on success — the
      // sheet stays open underneath so they land right back on it.
      pendingActionRef.current = () => { void submitRating(); };
      setAuthOpen(true);
      return;
    }
    setBusy(true);
    try {
      const r = REACTIONS.find((x) => x.rating === rating)!;
      const baseLabel = note.trim() || r.label.replace(/^\S+\s+/, "");
      const labels = [baseLabel.slice(0, 60), ...tags];

      // ---------------------------------------------------------------------
      // ROAD RESOLUTION (R1). This sheet used to write `coord` — the phone's
      // position, or a tapped point — straight into community_tags as the
      // public location of a road. It now has to name a road first.
      // ---------------------------------------------------------------------
      const outcome = await requestRoad({
        point: coord,
        mode,
        // A tapped point or a chosen address has no fix quality to judge.
        accuracyM: coordFromGps ? coordAccuracyM : null,
        userPlacedPoint: !coordFromGps,
      });

      if (outcome.status === "cancelled") {
        // "None of these". Nothing is saved, and the sheet stays open so the
        // note they typed isn't thrown away.
        setBusy(false);
        return;
      }

      if (outcome.status === "unresolved") {
        // No road data reachable. Hold it on the device with the raw point and
        // finish after reconnecting — the queue refuses to upload it until it
        // has resolved to a road.
        const { getEffectiveUserId } = await import("@/utils/anonId");
        const userId = await getEffectiveUserId();
        for (const label of labels) {
          enqueueTag({
            payload: {
              user_id: userId,
              lat: coord[1],
              lng: coord[0],
              travel_mode: mode,
              tag_label: label,
              sentiment: r.sentiment,
              pleasantness: label === labels[0] ? rating - 1 : null,
              source: "manual",
              report_kind: null,
            },
            mode,
            needsRoadResolution: true,
            resolveContext: {
              point: [coord[0], coord[1]],
              mode,
              accuracyM: coordFromGps ? coordAccuracyM : null,
              userPlacedPoint: !coordFromGps,
            },
            showToast: label === labels[0],
          });
        }
        clearDraft();
        onClose();
        return;
      }

      const road = outcome.road;
      const source = sourceForRoad(road, "manual");
      for (const [i, label] of labels.entries()) {
        await submitTag({
          // THE PUBLIC COORDINATE IS THE ROAD'S, NOT THE CONTRIBUTOR'S.
          lat: road.representative[1],
          lng: road.representative[0],
          mode,
          label,
          sentiment: r.sentiment,
          pleasantness: i === 0 ? rating - 1 : undefined,
          note: i === 0 ? (note.trim() || undefined) : undefined,
          source,
          segmentKeyOverride: road.segmentRef,
          snapDistM: road.distM,
          roadName: road.roadName ?? undefined,
        });
      }
      toast.success(
        road.roadName
          ? `Thanks — saved for ${road.roadName}`
          : "Thanks — this makes routes better for everyone",
      );
      clearDraft();
      onClose();
    } catch (e) {
      toast.error("Could not save", { description: e instanceof Error ? e.message : String(e) });
    } finally {
      setBusy(false);
    }
  };

  const submitRoad = async () => {
    if (!roadName.trim()) {
      toast.error("Name the road", { description: "What's this great road called?" });
      return;
    }
    if (!coord) {
      toast.error("Pick a spot on the road", { description: "Search the road, drop a pin, or use your location." });
      return;
    }
    if (!authLoading && !user) {
      pendingActionRef.current = () => { void submitRoad(); };
      setAuthOpen(true);
      return;
    }
    setBusy(true);
    try {
      await submitRoadNomination({
        name: roadName,
        coord,
        mode,
        why: roadWhy || undefined,
      });
      toast.success("Thanks — we'll feature this road on routes that drive it");
      setRoadName("");
      setRoadWhy("");
      onClose();
    } catch (e) {
      toast.error("Could not save", { description: e instanceof Error ? e.message : String(e) });
    } finally {
      setBusy(false);
    }
  };

  const submitStop = async () => {
    if (!stopName.trim()) {
      toast.error("Name the stop", { description: "What's this place called?" });
      return;
    }
    if (!coord) {
      toast.error("Need a location", { description: "Use your location, tap the map, or search the address." });
      return;
    }
    if (!authLoading && !user) {
      pendingActionRef.current = () => { void submitStop(); };
      setAuthOpen(true);
      return;
    }
    setBusy(true);
    try {
      const r = await nominateStop({
        name: stopName,
        why: stopWhy || undefined,
        lat: coord[1],
        lng: coord[0],
      });
      if (!r.ok) throw new Error(r.error);
      toast.success("Thanks — routes passing by will suggest it as a local pick");
      setStopName("");
      setStopWhy("");
      onClose();
    } catch (e) {
      toast.error("Could not save", { description: e instanceof Error ? e.message : String(e) });
    } finally {
      setBusy(false);
    }
  };

  const tabCount = 3 + (onAddPhoto ? 1 : 0) + (onAddTip ? 1 : 0);
  const tabColsClass =
    tabCount >= 5 ? "grid-cols-5" : tabCount === 4 ? "grid-cols-4" : "grid-cols-3";
  // Five tabs don't fit icon-beside-label on an iPhone (Guideline 4 lesson:
  // never crowd). Stack icon over label when full.
  const tabBtnCls = (active: boolean) =>
    `${tabCount >= 5
      ? "h-12 rounded-xl text-[11px] font-medium flex flex-col items-center justify-center gap-0.5"
      : "h-10 rounded-xl text-sm font-medium flex items-center justify-center gap-1.5"
    } transition-smooth ${active ? "bg-card shadow-soft text-foreground" : "text-muted-foreground"}`;

  // Per-tab footer CTA — extracted so we can pin it to the bottom of the
  // sheet on iPhone, where the sheet body scrolls independently. Keeps the
  // primary action (Save rating / Take a photo / Leave a tip) reachable
  // regardless of how far the user has scrolled through the chips list.
  const renderFooterCta = () => {
    if (tab === "rate") {
      // Disabled-button hint: when Save is greyed out the user often
      // doesn't know which input is missing — rating or location. Show
      // the specific thing they need to add. Real-drive UX audit (#56):
      // "I tapped Save and nothing happened" was a plausible failure
      // mode when both rating and coord were unset.
      let hint: string | null = null;
      if (!busy) {
        if (!rating && !coord) hint = "Pick a rating and a location first.";
        else if (!rating) hint = "Pick a rating first.";
        else if (!coord) hint = "Pick a location first.";
      }
      return (
        <div className="flex flex-col gap-1.5">
          <Button
            onClick={submitRating}
            disabled={busy || !rating || !coord}
            className="w-full h-12 rounded-2xl bg-pleasant text-primary-foreground"
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save rating"}
          </Button>
          {hint && (
            <p className="text-[11px] text-muted-foreground text-center">{hint}</p>
          )}
        </div>
      );
    }
    if (tab === "photo") {
      return (
        <Button
          onClick={() => { onAddPhoto?.(); onClose(); }}
          className="w-full h-12 rounded-2xl bg-pleasant text-primary-foreground"
        >
          <Camera className="w-4 h-4 mr-2" /> Take or pick a photo
        </Button>
      );
    }
    if (tab === "stop") {
      let hint: string | null = null;
      if (!busy) {
        if (!stopName.trim() && !coord) hint = "Name the stop and pick its location.";
        else if (!stopName.trim()) hint = "Name the stop first.";
        else if (!coord) hint = "Pick the stop's location.";
      }
      return (
        <div className="flex flex-col gap-1.5">
          <Button
            onClick={submitStop}
            disabled={busy || !stopName.trim() || !coord}
            className="w-full h-12 rounded-2xl bg-pleasant text-primary-foreground"
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Flag className="w-4 h-4 mr-2" /> Suggest this stop</>}
          </Button>
          {hint && <p className="text-[11px] text-muted-foreground text-center">{hint}</p>}
        </div>
      );
    }
    if (tab === "road") {
      let hint: string | null = null;
      if (!busy) {
        if (!roadName.trim() && !coord) hint = "Name the road and drop a pin on it.";
        else if (!roadName.trim()) hint = "Name the road first.";
        else if (!coord) hint = "Drop a pin anywhere on the road.";
      }
      return (
        <div className="flex flex-col gap-1.5">
          <Button
            onClick={submitRoad}
            disabled={busy || !roadName.trim() || !coord}
            className="w-full h-12 rounded-2xl bg-pleasant text-primary-foreground"
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Signpost className="w-4 h-4 mr-2" /> Nominate this road</>}
          </Button>
          {hint && <p className="text-[11px] text-muted-foreground text-center">{hint}</p>}
        </div>
      );
    }
    return (
      <div className="flex flex-col gap-1.5">
        <Button
          onClick={() => {
            if (!coord) {
              toast.error("Pick a location first");
              return;
            }
            onAddTip?.(coord, coordLabel);
            onClose();
          }}
          disabled={!coord}
          className="w-full h-12 rounded-2xl bg-pleasant text-primary-foreground"
        >
          <MessageSquare className="w-4 h-4 mr-2" /> Leave a tip
        </Button>
        {!coord && (
          <p className="text-[11px] text-muted-foreground text-center">
            Pick a location first.
          </p>
        )}
      </div>
    );
  };

  return (
    <>
    <Sheet open={open} onOpenChange={(o) => !o && onClose()}>
      {/*
        iPhone-friendly layout:
        - SheetContent is a flex column capped at 92dvh.
        - Default p-6/gap-4 from shadcn replaced with p-0/gap-0 so we can
          control inner padding per region.
        - Header pinned at the top, footer pinned at the bottom with the
          per-tab CTA + safe-area inset for the iPhone home indicator.
        - Middle scrolls independently (overscroll-contain so the map
          underneath doesn't scroll-chain on iOS).
        This guarantees the chips and the "Save rating" action stay
        visible regardless of scroll position.
      */}
      <SheetContent
        side="bottom"
        // z-[110] so the sheet sits ABOVE the navigation screen (z-[100]).
        // Without this it inherited the shadcn default (z-50) and opened
        // *behind* the full-screen nav view — the tag button appeared to do
        // nothing. Real-drive note: "tapped it, nothing happened."
        className="rounded-t-3xl max-h-[92dvh] flex flex-col p-0 gap-0 z-[110]"
      >
        <SheetHeader className="px-6 pt-6 pb-3 flex-shrink-0">
          <SheetTitle className="flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-pleasant" />
            Make routes better
          </SheetTitle>
          <SheetDescription className="sr-only">
            Rate a spot, add a photo or tip, suggest a stop, or nominate a great road.
          </SheetDescription>
        </SheetHeader>

        <div className="flex-1 min-h-0 overflow-y-auto overscroll-contain px-6 pb-3">
          {/* Pleasant scale — always at the top, drives the Rate tab. */}
          <div className="rounded-2xl border border-border bg-card p-3">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs uppercase tracking-wide text-muted-foreground">How pleasant?</span>
              {/* Live label — updates as you drag, so you always see what you're
                  rating. Replaces the old static row of all five labels. */}
              <span className={`text-sm font-semibold ${rating ? "text-pleasant" : "text-muted-foreground"}`}>
                {rating ? REACTIONS.find((r) => r.rating === rating)!.label : "Drag to rate"}
              </span>
            </div>
            <Slider
              min={1}
              max={5}
              step={1}
              value={[rating ?? 3]}
              onValueChange={(v) => { setRating(v[0]); if (tab !== "rate") setTab("rate"); }}
              className="my-2"
            />
          </div>

          {/* Tab switcher */}
          <div className={`grid ${tabColsClass} gap-2 mt-3 p-1 bg-muted rounded-2xl`}>
            <button type="button" onClick={() => setTab("rate")} className={tabBtnCls(tab === "rate")}>
              <Star className="w-4 h-4" /> Rate
            </button>
            {onAddPhoto && (
              <button type="button" onClick={() => setTab("photo")} className={tabBtnCls(tab === "photo")}>
                <Camera className="w-4 h-4" /> Photo
              </button>
            )}
            {onAddTip && (
              <button type="button" onClick={() => setTab("tip")} className={tabBtnCls(tab === "tip")}>
                <MessageSquare className="w-4 h-4" /> Tip
              </button>
            )}
            <button type="button" onClick={() => setTab("stop")} className={tabBtnCls(tab === "stop")}>
              <Flag className="w-4 h-4" /> Stop
            </button>
            <button type="button" onClick={() => setTab("road")} className={tabBtnCls(tab === "road")}>
              <Signpost className="w-4 h-4" /> Road
            </button>
          </div>

          {/* Location picker */}
          <div className="mt-3 space-y-2">
            <div className="flex items-center justify-between gap-2">
              <div className="flex items-center gap-2 text-xs min-w-0">
                <MapPin className="w-3.5 h-3.5 shrink-0 text-pleasant" />
                <span className="truncate">
                  {coord
                    ? <><span className="font-medium text-foreground">{coordLabel ?? "Selected spot"}</span>
                        <span className="text-muted-foreground"> · {coord[1].toFixed(4)}, {coord[0].toFixed(4)}</span></>
                    : <span className="text-muted-foreground">No location yet — pick one below</span>}
                </span>
              </div>
            </div>
            <div className={`grid ${onPickOnMap ? "grid-cols-3" : "grid-cols-2"} gap-1.5`}>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={useMyLocation}
                disabled={locating}
                className="h-9 rounded-xl text-xs"
              >
                {locating ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Crosshair className="w-3.5 h-3.5 mr-1" />}
                Here
              </Button>
              {onPickOnMap && (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => { onPickOnMap(); }}
                  className="h-9 rounded-xl text-xs"
                >
                  <Hand className="w-3.5 h-3.5 mr-1" /> Tap map
                </Button>
              )}
              <div className="relative col-span-1">
                <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
                <Input
                  value={addrQuery}
                  onChange={(e) => setAddrQuery(e.target.value)}
                  placeholder="Address"
                  className="h-9 pl-7 rounded-xl text-xs"
                />
              </div>
            </div>
            {(addrSearching || addrSuggestions.length > 0) && (
              <div className="rounded-xl border border-border bg-card max-h-48 overflow-y-auto">
                {addrSearching && addrSuggestions.length === 0 && (
                  <div className="px-3 py-2 text-xs text-muted-foreground flex items-center gap-2">
                    <Loader2 className="w-3 h-3 animate-spin" /> Searching…
                  </div>
                )}
                {addrSuggestions.map((s) => (
                  <button
                    key={s.id}
                    type="button"
                    onClick={() => pickAddress(s)}
                    className="w-full text-left px-3 py-2 hover:bg-muted transition-smooth border-b border-border last:border-b-0"
                  >
                    <div className="text-sm font-medium truncate">{s.name}</div>
                    <div className="text-[11px] text-muted-foreground truncate">{s.place}</div>
                  </button>
                ))}
              </div>
            )}
          </div>

          {tab === "rate" ? (
            <div className="mt-4 space-y-4">
              <div>
                <div className="text-xs uppercase tracking-wide text-muted-foreground mb-2">What's it like?</div>
                <div className="flex flex-wrap gap-1.5">
                  {QUICK_TAGS.map(({ label, kind }) => {
                    const on = tags.includes(label);
                    const offCls = kind === "neg"
                      ? "bg-card border-sky-500/40 text-sky-700 hover:border-sky-500"
                      : "bg-card border-emerald-500/40 text-emerald-700 hover:border-emerald-500";
                    const onCls = kind === "neg"
                      ? "bg-sky-500 text-primary-foreground border-transparent"
                      : "bg-emerald-500 text-primary-foreground border-transparent";
                    return (
                      <button
                        key={label}
                        type="button"
                        onClick={() => toggleTag(label)}
                        className={`px-2.5 py-1 rounded-full text-xs border transition-smooth ${on ? onCls : offCls}`}
                      >
                        {label}
                      </button>
                    );
                  })}
                </div>
              </div>

              <Input
                value={note}
                onChange={(e) => setNote(e.target.value)}
                maxLength={60}
                placeholder="One line about this spot (optional)"
                className="h-11 rounded-2xl"
              />
            </div>
          ) : tab === "photo" ? (
            <div className="mt-4 space-y-3">
              <p className="text-sm text-muted-foreground">
                Snap a shot of this spot — it helps people picture the route before they walk it.
              </p>
            </div>
          ) : tab === "tip" ? (
            <div className="mt-4 space-y-3">
              <p className="text-sm text-muted-foreground">
                Share something travelers should know about this spot — a heads-up, a must-do, or a local trick.
              </p>
            </div>
          ) : tab === "stop" ? (
            <div className="mt-4 space-y-3">
              <p className="text-sm text-muted-foreground">
                Know a place worth pulling over for? A viewpoint, a farm stand, the good ice cream.
                Routes that pass it will suggest it as a local pick.
              </p>
              <Input
                value={stopName}
                onChange={(e) => setStopName(e.target.value)}
                maxLength={120}
                placeholder="Name of the stop (e.g. Bear Mountain overlook)"
                className="h-11 rounded-2xl"
              />
              <Input
                value={stopWhy}
                onChange={(e) => setStopWhy(e.target.value)}
                maxLength={280}
                placeholder="Why is it worth it? (optional)"
                className="h-11 rounded-2xl"
              />
              <p className="text-[11px] text-muted-foreground">
                Set the location above — "Here" if you're at it, or search its address.
              </p>
            </div>
          ) : (
            <div className="mt-4 space-y-3">
              <p className="text-sm text-muted-foreground">
                Type the road's name — we'll find it. Just make sure the town is right.
              </p>
              <Input
                value={roadName}
                onChange={(e) => { setRoadName(e.target.value); setRoadPickedPlace(null); }}
                maxLength={80}
                placeholder="Road name (e.g. Round Hill Road)"
                className="h-11 rounded-2xl"
              />
              {roadSearchBusy && (
                <div className="flex items-center gap-2 text-xs text-muted-foreground px-1">
                  <Loader2 className="w-3.5 h-3.5 animate-spin" /> Searching…
                </div>
              )}
              {roadSuggestions.length > 0 && (
                <ul className="rounded-2xl border border-border bg-card overflow-hidden divide-y divide-border/60">
                  {roadSuggestions.map((s) => (
                    <li key={s.id}>
                      <button
                        type="button"
                        onClick={() => void pickRoadSuggestion(s)}
                        className="w-full text-left px-3 py-2.5 hover:bg-muted transition-smooth flex items-start gap-2"
                      >
                        <MapPin className="w-4 h-4 mt-0.5 text-primary shrink-0" />
                        <span className="min-w-0">
                          <span className="block text-sm font-medium truncate">{s.name}</span>
                          <span className="block text-xs text-muted-foreground truncate">{s.place}</span>
                        </span>
                      </button>
                    </li>
                  ))}
                </ul>
              )}
              {roadPickedPlace && (
                <div className="flex items-center gap-1.5 text-xs text-foreground/80 px-1">
                  <MapPin className="w-3.5 h-3.5 text-primary shrink-0" />
                  <span className="truncate">{roadPickedPlace}</span>
                  <button
                    type="button"
                    className="ml-1 text-primary underline-offset-2 underline"
                    onClick={() => setRoadPickedPlace(null)}
                  >
                    change
                  </button>
                </div>
              )}
              <Input
                value={roadWhy}
                onChange={(e) => setRoadWhy(e.target.value)}
                maxLength={240}
                placeholder="What makes it great? (optional)"
                className="h-11 rounded-2xl"
              />
              <p className="text-[11px] text-muted-foreground">
                Can't find it by name? Use "Tap map" above to drop a pin on the road instead.
              </p>
            </div>
          )}
        </div>

        {/* Sticky footer — always visible, regardless of scroll. Includes
         *  iPhone safe-area inset so the CTA clears the home indicator. */}
        <div
          className="flex-shrink-0 border-t border-border bg-card/95 backdrop-blur px-6 pt-3"
          style={{ paddingBottom: "calc(env(safe-area-inset-bottom) + 0.75rem)" }}
        >
          {renderFooterCta()}
        </div>
      </SheetContent>
    </Sheet>

    {roadPicker}
    <QuickAuthDialog
      open={authOpen}
      onOpenChange={(v) => {
        setAuthOpen(v);
        if (!v) pendingActionRef.current = null;
      }}
      reason="Sign in to make sure your contributions are saved to your account."
      onSuccess={() => {
        setAuthOpen(false);
        const fn = pendingActionRef.current;
        pendingActionRef.current = null;
        // Defer so the auth state has a chance to propagate first.
        if (fn) setTimeout(fn, 50);
      }}
    />
    </>
  );
};
